package org.catharsis.spendtel.feature.presentation.web

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.navigation.NavHostController
import kotlinx.coroutines.launch
import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.formatCurrency
import org.catharsis.spendtel.feature.presentation.ExpenseReportEvent
import org.catharsis.spendtel.feature.presentation.ExpenseReportViewModel
import org.catharsis.spendtel.feature.presentation.expensereport.ExpenseReportShareEffect
import org.catharsis.spendtel.feature.presentation.expensereport.ProvideExpenseReportShareHandlers
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.feature.share.canShareSummary
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun WebExpenseReportScreen(
    navHostController: NavHostController,
    reportId: String,
) {
    val viewModel = koinViewModel<ExpenseReportViewModel>(parameters = { parametersOf(reportId) })
    val state by viewModel.state.collectAsState()
    val scrollState = rememberScrollState()
    val chrome = rememberWebSessionChrome(navHostController)
    val snackbarHostState = remember { SnackbarHostState() }
    val coroutineScope = rememberCoroutineScope()

    ExpenseReportShareEffect(
        viewModel = viewModel,
        onCopiedToClipboard = {
            coroutineScope.launch {
                snackbarHostState.showSnackbar("Copied to clipboard")
            }
        },
        onImageDownloaded = {
            coroutineScope.launch {
                snackbarHostState.showSnackbar("Story image downloaded")
            }
        },
        onShareFailed = { message ->
            coroutineScope.launch {
                snackbarHostState.showSnackbar(message)
            }
        },
    )

    ProvideExpenseReportShareHandlers(
        onShareText = { content ->
            viewModel.postEvent(ExpenseReportEvent.OnShareCard(content))
        },
        onShareImage = { card ->
            viewModel.postEvent(ExpenseReportEvent.OnShareImageCard(card))
        },
    ) {
    Scaffold(
        modifier = Modifier
            .fillMaxSize()
            .imePadding(),
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = SpendTelReportColors.canvas,
    ) { innerPadding ->
    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(innerPadding),
    ) {
        WebAmbientPageBackground(modifier = Modifier.fillMaxSize())

        Column(modifier = Modifier.fillMaxSize()) {
            WebAppChrome(
                userName = chrome.userName,
                email = chrome.email,
                creditsSummary = chrome.creditsSummary,
                accountMenuExpanded = chrome.accountMenuExpanded,
                onLogoClick = chrome.onLogoClick,
                onAccountMenuToggle = chrome.onAccountMenuToggle,
                onAccountMenuDismiss = chrome.onAccountMenuDismiss,
                onOpenSettings = chrome.onOpenSettings,
                onSignOut = chrome.onSignOut,
            )

            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth(),
            ) {
                when {
                    state.isLoading && state.report == null -> {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center,
                        ) {
                            CircularProgressIndicator(color = SpendTelReportColors.violet)
                        }
                    }

                    state.error != null && state.report == null -> {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(32.dp),
                            contentAlignment = Alignment.Center,
                        ) {
                            Box(
                                modifier = Modifier
                                    .widthIn(max = webPageMaxWidth)
                                    .fillMaxWidth(),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(
                                    text = state.error.orEmpty(),
                                    color = MaterialTheme.colorScheme.error,
                                    style = MaterialTheme.typography.bodyLarge,
                                )
                            }
                        }
                    }

                    state.report != null -> {
                        val report = state.report!!
                        WebViewportScrollColumn(
                            scrollState = scrollState,
                            modifier = Modifier.fillMaxSize(),
                        ) {
                            Column(
                                modifier = Modifier.padding(horizontal = 24.dp, vertical = 28.dp),
                                verticalArrangement = Arrangement.spacedBy(28.dp),
                            ) {
                                WebBackLink(
                                    label = "Back to dashboard",
                                    onClick = { navHostController.popBackStack() },
                                )

                                val storyTitle = report.statementPeriodLabel

                                val breadcrumbSegments = buildList {
                                    add("Dashboard")
                                    add(storyTitle)
                                    storyDetailLabel(report)?.let { add(it) }
                                }

                                WebPageBreadcrumb(
                                    segments = breadcrumbSegments,
                                )

                                WebReportPageHeader(
                                    storyTitle = storyTitle,
                                    personalityLabel = report.financialPersonality?.label,
                                    canShare = report.canShareSummary(),
                                    linkLabel = if (state.isCreatingShareLink) {
                                        "Creating link…"
                                    } else {
                                        "Share link"
                                    },
                                    onShareLink = {
                                        viewModel.postEvent(
                                            ExpenseReportEvent.OnCreatePublicLink(report),
                                        )
                                    },
                                    onShareStory = {
                                        viewModel.postEvent(
                                            ExpenseReportEvent.OnShareSummary(report),
                                        )
                                    },
                                )

                                WebExpenseReportBody(
                                    report = report,
                                    modifier = Modifier.fillMaxWidth(),
                                )
                            }
                        }
                    }
                }
            }
        }
    }
    }
    }
}

private fun storyDetailLabel(report: ExpenseReport): String? {
    return report.financialPersonality?.label
        ?: report.monthlyStory?.headline
        ?: report.summary?.let { formatCurrency(it.totalSpending, it.currency) }
}

@Composable
private fun WebReportPageHeader(
    storyTitle: String,
    personalityLabel: String?,
    canShare: Boolean,
    linkLabel: String,
    onShareLink: () -> Unit,
    onShareStory: () -> Unit,
) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val compact = maxWidth < webCompactBreakpoint

        val titleBlock = @Composable {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    text = storyTitle,
                    style = MaterialTheme.typography.displaySmall,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                )
                personalityLabel?.let { personality ->
                    Text(
                        text = personality,
                        style = MaterialTheme.typography.titleMedium,
                        color = SpendTelReportColors.violet,
                        fontWeight = FontWeight.Medium,
                    )
                }
            }
        }

        if (compact) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                titleBlock()
                if (canShare) {
                    WebReportShareActions(
                        linkLabel = linkLabel,
                        onShareLink = onShareLink,
                        onShareStory = onShareStory,
                        compact = true,
                    )
                }
            }
        } else {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(modifier = Modifier.weight(1f)) {
                    titleBlock()
                }
                if (canShare) {
                    WebReportShareActions(
                        linkLabel = linkLabel,
                        onShareLink = onShareLink,
                        onShareStory = onShareStory,
                        compact = false,
                    )
                }
            }
        }
    }
}
